import { PatientAgeUnit } from 'pages/Automations/types/SendConditionPatientAge';
import { TimeFormatValue } from 'shared/components/WildCardInputGroup/WildcardTimeFormatSelect';
import {
  GetAutomationEditorDataQuery,
  AutomatedTriggerType,
  WorkflowSendingHours,
  WorkflowVisitType,
  WorkflowEventTriggerType,
  WorkflowEvent,
  ConditionAttribute,
  ConditionOperator,
} from 'shared/types/graphql';
import { AutomationDraft, AutomationType, TimeIncrement } from '../../types/AutomationDraft';
import { isSendConditionAttribute, SendConditions } from '../../types/AutomationSendConditions';
import { convertMinutesToTimeDisplay, inMinutes } from './time';

export const initialAutomationDraft: AutomationDraft = {
  automationId: null,
  automationName: null,
  isPublished: false,
  automationType: null,
  sendTiming: {
    triggerType: AutomatedTriggerType.Before,
    timeValue: 1,
    timeIncrement: TimeIncrement.Hours,
    timeOfDay: null,
  },
  sendConditions: {
    AppointmentTypeId: {
      createdAt: String(new Date()),
      attribute: ConditionAttribute.AppointmentTypeId,
      operator: ConditionOperator.In,
      operand: {
        value: [],
      },
    },
  },
  conditionSets: [],
  forms: null,
  requestOptions: null,
  sendFrequency: WorkflowVisitType.Patient,
  sendHours: WorkflowSendingHours.All24Hours,
  triggerType: WorkflowEventTriggerType.Automated,
  notificationMessage: null,
  timeFormat: null,
  prompts: [],
};

export const childAutomationEventTypes: AutomationType[] = [
  WorkflowEvent.AppointmentClientArrival,
  WorkflowEvent.AppointmentReminder,
  WorkflowEvent.AppointmentConfirmReminder,
  WorkflowEvent.AppointmentFollowup,
];

export const convertAutomationPersistedToDraft = ({
  automation,
  conditionSets,
}: {
  automation: GetAutomationEditorDataQuery['findUniqueWorkflowEventSetting'];
  // TODO: Make `conditionSets` non-optional
  conditionSets?: GetAutomationEditorDataQuery['findManyConditionSet'];
}): AutomationDraft => {
  if (!automation) {
    return initialAutomationDraft;
  }

  const {
    id,
    name,
    isPublished,
    event,
    automatedTriggerType,
    automatedTriggerInMinutes,
    automatedTriggerTimeOfDay,
    triggeringFormTemplates,
    widgetRequestTypes,
    visitType,
    sendingHours,
    triggerType,
    notificationBody,
    timeFormat,
    actions,
  } = automation;

  // Default time increment for lapsed pet parent is 24 months
  // Defualt time increment for all other automation types is 1 hour
  const defaultTimeIncrement = event === WorkflowEvent.LapsedPetParent ? inMinutes.Months * 24 : 60;

  const { timeValue, timeIncrement } = convertMinutesToTimeDisplay({
    timeInMinutes: automatedTriggerInMinutes || defaultTimeIncrement,
    automationType: event,
    isTimeOfDaySpecified: !!automatedTriggerTimeOfDay,
  });

  const prompts = actions.map((action) => {
    let invoiceAmount = '';

    if (action.config.invoiceAmount) {
      const invoiceString = action.config.invoiceAmount.toString();
      const dollars = invoiceString.slice(0, invoiceString.length - 2);
      const cents = invoiceString.slice(invoiceString.length - 2);

      invoiceAmount = `${dollars}.${cents}`;
    }

    return {
      id: action.id,
      channelStatusChangeId: action.channelStatusChangeId || null,
      order: action.order,
      promptType: action.promptType,
      config: {
        ...action.config,
        invoiceAmount,
      },
      triggerDelayInMilliseconds: action.workflowEventTriggerDelayInMilliseconds || null,
      triggerActionNumber: action.workflowEventTriggerActionNumber || null,
      triggerWorkflowId: action.triggerWorkflowId || null,
    };
  });

  let sendConditions: SendConditions = {};

  /**
   * When at least one condition set is passed to this function, we assume the clinic has the feature flag,
   * so we use the first (currently only) condition set to populate send conditions.
   */
  if (conditionSets?.length) {
    const conditionSet = conditionSets[0];

    sendConditions = conditionSet.conditions.reduce((prev, curr) => {
      if (isSendConditionAttribute(curr.attribute)) {
        const condition = JSON.parse(
          JSON.stringify({
            createdAt: String(curr.createdAt),
            attribute: curr.attribute,
            operator: curr.operator,
            operand: curr.operand,
          }),
        );

        const { attribute, operand } = condition;
        if (attribute === ConditionAttribute.PetAgeInYears) {
          if (!Number.isInteger(Number(operand.value))) {
            condition.operand.value = Math.round(12 * operand.value);
            condition.operand.displayUnit = PatientAgeUnit.Months;
          } else {
            condition.operand.displayUnit = PatientAgeUnit.Years;
          }
        }

        prev[curr.attribute as keyof SendConditions] = condition;
      }
      return prev;
    }, {} as SendConditions);
  }

  return {
    automationId: id,
    automationName: name,
    isPublished: !!isPublished,
    automationType: event,
    sendTiming: {
      triggerType: automatedTriggerType,
      timeValue: timeValue,
      timeOfDay: automatedTriggerTimeOfDay,
      timeIncrement,
    },
    sendConditions,
    conditionSets: conditionSets || [],
    forms: triggeringFormTemplates.map(({ id }) => id),
    requestOptions: widgetRequestTypes.map(({ id }) => id),
    sendFrequency: visitType,
    sendHours: sendingHours,
    triggerType,
    notificationMessage: notificationBody || null,
    timeFormat: (timeFormat as TimeFormatValue) || null,
    prompts,
  };
};
